import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useSiteContent } from "@/context/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

function Stat({ value, label, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const to = Number(value) || 0;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <div ref={ref} data-testid={`about-stat-${String(label).toLowerCase().replace(/\s+/g, '-')}`} className="border-t border-warm pt-6">
      <div className="font-serif-display gold-ink text-5xl md:text-6xl leading-none">
        {n.toLocaleString()}{suffix}
      </div>
      <div className="mt-3 text-xs uppercase tracking-[0.35em] text-muted-warm">{label}</div>
    </div>
  );
}

export default function About() {
  const { content } = useSiteContent();
  const about = content?.about || {};
  const stats = about.stats || [];

  return (
    <section id="story" data-testid="about-section" className="relative section-pad bg-bg overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs tracking-[0.4em] uppercase text-[color:var(--gold-hi)]"
          >
            {about.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mt-6 font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tight text-cream"
          >
            {about.title_line_1} <br />
            <span className="gold-ink italic">{about.title_line_2}</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mt-10 space-y-6 text-cream/85 leading-relaxed max-w-xl text-lg font-light"
          >
            <p data-testid="about-paragraph-1">{about.paragraph_1}</p>
            <p data-testid="about-paragraph-2">{about.paragraph_2}</p>
            <p data-testid="about-paragraph-3">{about.paragraph_3}</p>
          </motion.div>

          <div className="mt-14 grid grid-cols-3 gap-6">
            {stats.map((s, idx) => (
              <Stat key={idx} value={s.value} label={s.label} suffix={s.suffix || ""} />
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1 }}
          className="md:col-span-5 relative"
        >
          <div className="relative aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-warm">
            <img src={resolveAssetUrl(about.interior_image)} alt="Restaurant interior" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(26,14,8,0.7)] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="font-serif-display italic text-2xl text-cream leading-tight">
                {about.quote_1}<br/>{about.quote_2}
              </p>
              <p className="mt-3 text-xs tracking-[0.35em] uppercase text-[color:var(--gold-hi)]">
                {about.quote_attribution}
              </p>
            </div>
          </div>

          {about.hours_badge && (
            <div className="absolute -bottom-6 -left-6 hidden md:block">
              <div className="rounded-full w-28 h-28 bg-[color:var(--olive)]/90 flex items-center justify-center text-center text-[color:var(--bg)] font-serif-display leading-tight rotate-[-8deg] shadow-gold">
                <span className="text-xs px-3 whitespace-pre-line">
                  {about.hours_badge}
                </span>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
